import { useEffect, useRef, useState } from 'react';
import { selectCurrentUser } from '../../../../store/auth/selectors';
import { selectOnlineUsers } from '../../../../store/users/selectors';
import styles from './PatientDiscussion.module.scss';
import { AttachmentErrorModal } from './components/AttachmentErrorModal/AttachmentErrorModal';
import { MessageErrorModal } from './components/MessageErrorModal';
import cx from 'classnames';
import { Attachments, BlockWrapperWithSpinner, Chat, Icon, RolePermissionChecker, Text } from 'components';
import { useDevice } from 'hooks';
import { selectAppModals } from 'store/app/selectors';
import { dashboardActions } from 'store/dashboard/slice';
import { selectCurrentPatientCase, selectPatientActionIsLoading } from 'store/patients/selectors';
import { useDispatch, useSelector } from 'store/utils';
import { DiscussionTab, UserRole } from 'types';

type Props = {
  height?: number;
};

export const PatientDiscussion = ({ height }: Props) => {
  const [currentTab, setCurrentTab] = useState(DiscussionTab.CHAT);
  const containerRef = useRef<HTMLDivElement>(null);
  const uploadMessageFilesIsLoading = useSelector(selectPatientActionIsLoading('uploadMessageFiles'));
  const modals = useSelector(selectAppModals);
  const currentUser = useSelector(selectCurrentUser);
  const onlineUsers = useSelector(selectOnlineUsers);
  const currentPatientCase = useSelector(selectCurrentPatientCase);
  const dispatch = useDispatch();

  const { isTablet } = useDevice();

  const isDoctorOnline = onlineUsers.includes(currentPatientCase?.assignedDoctorId || '');
  const isClinicOnline = onlineUsers.includes(currentPatientCase?.clinicId || '');
  const currentRole = currentUser?.role;
  const isOtherUserOnline = currentRole === UserRole.CLINIC ? isDoctorOnline : isClinicOnline;
  const patientCaseId = currentPatientCase?.id;

  const onDismissPatientCase = async () => {
    try {
      if (!patientCaseId) return;
      await dispatch(dashboardActions.dismissPatientCase(patientCaseId)).unwrap();
    } catch {
      console.log('failed to dismiss ', patientCaseId);
    }
  };

  useEffect(() => {
    if (containerRef.current && height) {
      const deviceHeight = window.innerHeight;
      containerRef.current.style.height = `${deviceHeight - height - 60}px`;
    }
  }, [height]);

  const showOnlineStatusLabel =
    currentPatientCase?.assignedDoctorId &&
    (currentPatientCase?.assignedDoctorId === currentUser?.id || currentPatientCase?.clinicId === currentUser?.id);

  return (
    <div className={styles.container} ref={containerRef}>
      {uploadMessageFilesIsLoading && <BlockWrapperWithSpinner isLoading={uploadMessageFilesIsLoading} />}
      <div className={styles.header}>
        <Text type="h4">Discussion</Text>
        <div className={styles.attachments} onClick={() => setCurrentTab(DiscussionTab.ATTACHMENTS)}>
          <Icon name="Files" className={styles.icon} />
          <Text type="caption">Attachments</Text>
        </div>
      </div>
      <div className={styles.optionsWrapper}>
        {showOnlineStatusLabel && (
          <div
            className={cx(
              styles.onlineStatusContainer,
              isOtherUserOnline ? styles.onlineStatusContainerOnline : styles.onlineStatusContainerOffline,
            )}>
            <div className={styles.onlineStatusDot} />
            <Text type="caption">{isOtherUserOnline ? 'Online' : 'Away'}</Text>
          </div>
        )}
        {!isTablet && (
          <RolePermissionChecker rolesAllowedToView={[UserRole.CLINIC, UserRole.DOCTOR, UserRole.ADMIN]}>
            <div onClick={onDismissPatientCase} className={styles.dismissWrapper}>
              <Icon name="Cross" className={styles.icon} />
              <Text type="body2">Dismiss message notification</Text>
            </div>
          </RolePermissionChecker>
        )}
      </div>
      {currentTab === DiscussionTab.CHAT ? (
        <Chat setCurrentTab={() => setCurrentTab(DiscussionTab.ATTACHMENTS)} />
      ) : (
        <Attachments setDiscussionTab={() => setCurrentTab(DiscussionTab.CHAT)} />
      )}
      {modals.messagingErrorModal && <MessageErrorModal />}
      {modals.attachmentErrorModal && <AttachmentErrorModal />}
    </div>
  );
};
